import { useState } from "react";

import type { DocumentSource } from "../../types/chat";

interface SourcesListProps {
  sources: DocumentSource[];
  /** When true, sources can be collapsed/expanded */
  collapsible?: boolean;
  /** When collapsible is true, start collapsed by default */
  defaultCollapsed?: boolean;
}

/**
 * Get appropriate icon for document type
 */
function getDocumentTypeIcon(documentType?: string): string {
  switch (documentType) {
    case "statuten":
      return "📜";
    case "reglement":
      return "📋";
    case "beleid":
      return "📑";
    case "gedragscode":
      return "🤝";
    case "besluit":
      return "✅";
    case "notulen":
      return "📝";
    case "template":
      return "📄";
    default:
      return "📄";
  }
}

/**
 * Get Dutch label for document type
 */
function getDocumentTypeLabel(documentType?: string): string {
  switch (documentType) {
    case "statuten":
      return "Statuten";
    case "reglement":
      return "Reglement";
    case "beleid":
      return "Beleid";
    case "gedragscode":
      return "Gedragscode";
    case "besluit":
      return "Besluit";
    case "notulen":
      return "Notulen";
    case "template":
      return "Template";
    case "overig":
      return "Overig";
    default:
      return documentType ?? "Document";
  }
}

/**
 * Displays a list of source documents used to answer a chat message
 */
export function SourcesList({
  sources,
  collapsible = false,
  defaultCollapsed = false,
}: SourcesListProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  if (!sources || sources.length === 0) {
    return null; // Don't show anything if no sources
  }

  const toggleCollapsed = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  return (
    <div className="mt-4 rounded-md border border-primary/20 bg-primary-container/40 p-4">
      <h3
        className={`mb-3 font-semibold text-sm text-on-primary-container flex items-center gap-2 ${
          collapsible ? "cursor-pointer hover:text-primary" : ""
        }`}
        onClick={toggleCollapsed}
        role={collapsible ? "button" : undefined}
        aria-expanded={collapsible ? !isCollapsed : undefined}
      >
        📚 Bronnen ({sources.length})
        {collapsible && <span className="ml-auto text-xs">{isCollapsed ? "▶" : "▼"}</span>}
      </h3>

      {!isCollapsed && (
        <ul className="space-y-3">
          {sources.map((source) => (
            <li
              key={source.id}
              className="bg-surface-container-lowest rounded-md p-3 border border-outline-variant/50 shadow-1"
            >
              {/* Document name with icon */}
              <div className="flex items-start gap-2">
                <span className="text-lg" aria-hidden="true">
                  {getDocumentTypeIcon(source.documentType)}
                </span>
                <div className="flex-1 min-w-0">
                  {/* Document name as clickable link */}
                  {source.url ? (
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-primary hover:text-on-primary-container hover:underline break-words"
                    >
                      {source.name}
                      <span className="ml-1 text-xs" aria-hidden="true">
                        ↗
                      </span>
                    </a>
                  ) : (
                    <div className="font-medium text-on-surface break-words">{source.name}</div>
                  )}

                  {/* Metadata badges */}
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {source.documentType && (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-secondary-container text-on-secondary-container rounded-sm">
                        {getDocumentTypeLabel(source.documentType)}
                      </span>
                    )}

                    {source.body && (
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-tertiary-container text-on-tertiary-container rounded-sm">
                        {source.body}
                      </span>
                    )}

                    {source.status && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-sm ${
                          source.status === "definitief"
                            ? "bg-primary-container text-on-primary-container"
                            : "bg-tertiary-container text-on-tertiary-container"
                        }`}
                      >
                        {source.status === "definitief" ? "✓ Definitief" : "◐ Concept"}
                      </span>
                    )}
                  </div>

                  {/* Date row */}
                  {source.formattedDate && (
                    <div className="mt-2 text-xs text-on-surface-variant">
                      <span className="flex items-center gap-1">
                        <span aria-hidden="true">📅</span>
                        {source.formattedDate}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
